/**
 * Created by andreaskarantzas on 09.04.2021.
 */
import { makeAutoObservable } from 'mobx';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { v1ParamsToV3Params } from './exploreV3Params';
import { ExploreEntryQueryRequest } from './ExploreEntryQueryRequest';
import { ExploreEntry, createExploreEntry } from './ExploreEntry';
import { RecurringPattern, createRecurringPattern } from '../Coach/Schedule/RecurringPattern';
import dayjs, { Dayjs } from 'dayjs';
import { SubscribedEventEntry, createSubscribedEventEntry } from '../Coach/Schedule/SubscribedEventEntry';
import { Duration } from 'dayjs/plugin/duration';
import { Instructor, createInstructor } from './Instructor';

export type SubscribableEntryBase = ExploreEntry & {
  startDate: string;
  startTime: string;
  timezone: string;
  duration: string;
  recurringPattern?: RecurringPattern;
  dates: Date[];
  hosts: Instructor[];
  canJoinMeeting: boolean;
  readonly startTimeFixed: string | undefined;
  readonly startDateTime: Dayjs | undefined;
  readonly localStartDateTime: Dayjs | undefined;
  readonly dayjsDuration: Duration;
  readonly durationDayjs: Dayjs;
  readonly nextDateIndex: number;
  readonly nextStartDateIndex: number;
  readonly hasMoreMeetings: boolean;
  readonly nextDate: Dayjs | undefined;
  readonly nextStartDate: Dayjs | undefined;
  readonly nextOrLastDate: Dayjs | undefined;
  readonly event: SubscribedEventEntry;
  nextDateWithinTimeRange(startDate: Dayjs): Dayjs | undefined;
};

export type SubscribableEntryType = 'onlineMeeting' | 'offlineMeeting' | 'webinar';

export type SubscribableEntry = SubscribableEntryBase & {
  type: SubscribableEntryType;
};


export function createSubscribableEntryBase<T extends SubscribableEntryType>(
  json: Partial<SubscribableEntryBase> | undefined,
  type: T,
) {
  return {
    ...createExploreEntry({ ...json, type }),
    type,
    startDate: json?.startDate ?? dayjs().add(1, 'day').format('YYYY-MM-DD'),
    startTime: json?.startTime ?? dayjs().add(1, 'day').format('HH:mm:ss'),
    timezone: json?.timezone ?? 'Europe/Zurich',
    duration: json?.duration ?? 'PT1H',
    recurringPattern: json?.recurringPattern ? createRecurringPattern(json.recurringPattern as any) : undefined,
    dates: ((json as any)?.dates ?? []).map((d: string) => new Date(d)),
    hosts: (json?.hosts ?? []).map((h: any) => createInstructor(h)),
    canJoinMeeting: false,

    get startTimeFixed(): string | undefined {
      if (this.startTime?.length === 5) return `${this.startTime}:00`;
      if ((this.startTime?.length ?? 0) >= 8) return this.startTime.substring(0, 8);
      return undefined;
    },

    get startDateTime(): Dayjs | undefined {
      if (this.startDate) {
        const time = this.startTimeFixed ?? '00:00';
        return dayjs(`${this.startDate} ${time}`, 'YYYY-MM-DD HH:mm');
      }
      return undefined;
    },

    get localStartDateTime(): Dayjs | undefined {
      return this.startDateTime ? dayjs(this.startDateTime.toDate()) : undefined;
    },

    get dayjsDuration(): Duration {
      return dayjs.duration(this.duration);
    },

    get durationDayjs(): Dayjs {
      return dayjs.utc(0).add(this.dayjsDuration);
    },

    get nextDateIndex(): number {
      const now = dayjs();
      return this.dates
        .map((date) => [dayjs(date), dayjs(date).add(dayjs.duration(this.duration))])
        .findIndex(([start, end]) => (!now.isBefore(start) && !now.isAfter(end)) || now.isBefore(start));
    },

    get nextStartDateIndex(): number {
      const now = dayjs();
      return this.dates
        .slice()
        .sort((a, b) => dayjs(a).diff(dayjs(b)))
        .map((date) => dayjs(date))
        .findIndex((start) => now.isBefore(start));
    },

    get hasMoreMeetings(): boolean {
      return this.dates.length > 1 && this.nextDateIndex !== -1 && this.nextDateIndex < this.dates.length - 1;
    },

    get nextDate(): Dayjs | undefined {
      const nextDate = this.dates[this.nextDateIndex];
      return nextDate ? dayjs(nextDate) : undefined;
    },

    get nextStartDate(): Dayjs | undefined {
      const nextDate = this.dates[this.nextStartDateIndex];
      return nextDate ? dayjs(nextDate) : undefined;
    },

    get nextOrLastDate(): Dayjs | undefined {
      const nextDate = this.dates[this.nextDateIndex] ?? this.dates[this.dates.length - 1];
      return nextDate ? dayjs(nextDate) : undefined;
    },

    get event(): SubscribedEventEntry {
      return createSubscribedEventEntry({
        objectId: this.id,
        objectType: this.type,
        duration: this.duration,
      });
    },

    nextDateWithinTimeRange(startDate: Dayjs): Dayjs | undefined {
      return this.dates.map((d) => dayjs(d)).find((d) => !d.isBefore(startDate));
    },
  };
}

export function createSubscribableEntry(json?: Partial<SubscribableEntry>): SubscribableEntry {
  return makeAutoObservable(createSubscribableEntryBase(json, 'webinar'));
}

import type { OnlineMeetingEntry } from './OnlineMeetingEntry';
import type { OfflineMeetingEntry } from './OfflineMeetingEntry';
import type { WebinarEntry } from './WebinarEntry';

export type SubscribableEntryUnion = SubscribableEntry | OnlineMeetingEntry | OfflineMeetingEntry | WebinarEntry;

export const SubscribableEntryApi = {
  find(request: Omit<ExploreEntryQueryRequest, 'type'>): Promise<SubscribableEntry[]> {
    return HttpBackend.get(`/coach/explore/v3/entries`, v1ParamsToV3Params({ ...request, type: 'onlineMeeting' })).then((res) =>
      (res ?? []).map((r: any) => createSubscribableEntry(r)),
    );
  },

  findOne(id: string): Promise<SubscribableEntry> {
    return HttpBackend.get(`/coach/explore/v3/entries/${id}`).then((res) => createSubscribableEntry(res));
  },
};
